import { useEffect, useState } from 'react';
import { badges as badgesApi, type BadgeDto, type BadgeListDto } from '../net/api';

/** Badges shown on a profile card. */
export function BadgeRow({ badges }: { badges: BadgeDto[] }) {
  return (
    <div className="badge-row" aria-label="Badges">
      {badges.map((b) => (
        <span key={b.key} className="badge-chip" style={{ ['--b' as string]: b.color }} title={b.description}>
          <span aria-hidden="true">{b.icon}</span>
          {b.name}
        </span>
      ))}
    </div>
  );
}

/**
 * Picks the badges shown on a profile. In the account panel (no childId) players
 * choose up to 5; players aged 13 and under see theirs read-only, because a parent
 * picks up to 3 for them from the family dashboard (childId set).
 */
export function BadgePicker({ childId }: { childId?: number }) {
  const [data, setData] = useState<BadgeListDto>();
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string }>();
  useEffect(() => {
    (childId ? badgesApi.child(childId) : badgesApi.mine()).then(setData, (e: Error) => setMsg({ kind: 'err', text: e.message }));
  }, [childId]);
  if (!data) return msg ? <p className="form-error">{msg.text}</p> : null;
  const { badges: list, limit } = data;
  const who = childId ? 'They' : 'You';
  if (!list.length) return <p className="muted small-print">No badges yet. {who} can earn them in the Deep Mine, at events and more!</p>;
  const readOnly = data.parentChooses && !childId;
  const shown = list.filter((b) => b.featured).map((b) => b.key);
  const toggle = async (key: string) => {
    const keys = shown.includes(key) ? shown.filter((k) => k !== key) : [...shown, key];
    if (keys.length > limit) return setMsg({ kind: 'err', text: `You can show up to ${limit} badges. Untick one first.` });
    try {
      setData(await (childId ? badgesApi.featureForChild(childId, keys) : badgesApi.feature(keys)));
      setMsg(undefined);
    } catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message });
    }
  };
  return (
    <div className="badge-pick">
      <p className="muted small-print">
        {readOnly
          ? 'Your parent chooses which badges show on your profile (up to 3). Ticked ones are showing now.'
          : childId
            ? `Tick up to ${limit} badges to show on your child's profile.`
            : `Tick up to ${limit} badges to show on your profile.`}
      </p>
      {list.map((b) => (
        <label key={b.key}>
          <input type="checkbox" checked={b.featured} disabled={readOnly} onChange={() => void toggle(b.key)} />
          <span className="badge-chip" style={{ ['--b' as string]: b.color }}><span aria-hidden="true">{b.icon}</span>{b.name}</span>
          <span className="muted small-print">{b.description}</span>
        </label>
      ))}
      {msg && <p className={msg.kind === 'ok' ? 'note-ok' : 'form-error'} role="status">{msg.text}</p>}
    </div>
  );
}
